import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';

import Image from '../../../Core/Image';
import Container from '../../../Core/Container';

// * Background wrapper
export const Wrapper = styled.div`
  background-color: ${themeGet('colors.primary')};
  overflow: hidden;

  /* Only render background + image size when on bigger breakpoints */
  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    background-image: url(${({ background }) => background});
    background-size: cover;
    background-position: center;
    height: 550px;
    max-height: 550px;
  }
`;

// * Grid Container (Applieds padding, centering, bg circle)
export const StyledContainer = styled(Container)`
  display: flex;
  flex-direction: column;

  ${Wrapper}.text-alignment---center & {
    padding: 56px 0 0;
    justify-content: flex-start;
    align-items: center;
    height:auto;
  }
  ${Wrapper}.text-left---image-right & {
    padding: 45px 0 0;
    height: 100%;

    // * BG circle
    &::after {
      content: '';
      background-repeat: no-repeat;
      background-position: center;
      background-size: cover;
  
      position: absolute;
      bottom: 0;
      right: -75px;
  
      width: calc(100% + 100px);
      margin: 0 -50px;
      height: 100%;
      max-height: 300px;
      background-size: contain;
      background-position: bottom;
      left: auto;
      right: auto;
    }
  
      @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
        background-image: linear-gradient(to right, rgba(0,0,0,1), rgba(0,0,0,0));
        width:100%;
        &::after {
          max-height: 100%;
          width: 100vw;
          margin: 0;
          background-position: center;
          background-size: cover;
        }
      }
  
      @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
        &::after {
          max-height: 100%;
          width: 100vw;
          background-position: center;
          background-size: cover;
        }
      }
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    &.reverse {
      flex-direction: row-reverse;
    }

    ${Wrapper}.text-alignment---center & {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 70px 30px 0;
    }

    ${Wrapper}.text-left---image-right &{

      flex-direction: row;
      justify-content: space-between;
      align-items: flex-start;
      padding: 45px 0 0;

      // * BG circle 
      &::after {
        position: absolute;
        bottom: 0;
        height: 90%;
        max-height: 100%;
        max-width: 100%;
  
        background-position: bottom;
        background-size: contain;
  
        width: 60%;
        right: -20%;
      }
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    
    ${Wrapper}.text-alignment---center & {
      padding: 70px 85px 0;
    }
    ${Wrapper}.text-left---image-right & {
      padding: 70px 0 0;
      // * BG circle
      &::after {
        position: absolute;
        bottom: 0;
        height: 90%;
        max-height: 100%;
        max-width: 100%;
  
        background-position: bottom;
        background-size: contain;
  
        width: 60%;
        right: -20%;
      }
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xl')}) {

    ${Wrapper}.text-left---image-right & {
      padding-left: 90px;
      // * BG circle
      &::after {
        width: 55%;
      }
    }
  }
`;

// * Container for the title, copy, and button
export const Content = styled.div`
  width: 100%;
  margin-top: auto;
  margin-bottom: auto;
  margin-left: 5%;
  max-width: 764px;
  padding-bottom: 75px;
  z-index: 1;

  /* Hero title */
  > h1,
  > h2 {
    color: ${themeGet('colors.text-primary')};
    margin-bottom: 20px;

    @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
      color: #f5bd5a;
    }

    span {
      line-height: 35px;
      display: block;
      color: ${themeGet('colors.secondary')};
    }
  }

  /* Body Copy */
  p {
    color: ${themeGet('colors.text-primary')};
    margin-bottom: 20px;
    font-size: 18px;
    font-weight: 500;
    letter-spacing: -0.41px;
    line-height: 24px;
    /* fix overflow */
    width: 100%;
  }

  ${StyledContainer}.reduced-width & {
    max-width: 620px;
  }

  ${Wrapper}.text-alignment---center & {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-left: 0;
    padding-bottom: 40px;

    > a {
      width: 90%;
      @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
        width: 50%;
      }
    }

    > h1,
    > h2 {
      font-size: 36px;
      text-align: center;
      letter-spacing: -0.5px;

      @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
        color: ${themeGet('colors.text-primary')};
      }
    }
    > p {
      width: 90%;
      text-align: center;
      line-height: 26px;
      font-weight: 400;
      margin-bottom: 40px;
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    padding-bottom: 54px;

    > h1,
    > h2 {
      span {
        line-height: 46px;
      }
    }

    ${Wrapper}.text-left---image-right & {
      p {
        max-width: 400px;
      }
    }

    ${StyledContainer}.reduced-width & {
      min-width: 350px;
      padding-right: 35px;
    }

    ${Wrapper}.text-alignment---center & {
      padding: 25px 50px 75px 50px;
      > p {
        margin-bottom: 19px;
      }
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    padding-bottom: 80px;

    > h1,
    > h2 {
      margin-bottom: 30px;
    }

    ${Wrapper}.text-left---image-right & {
      p {
        margin-bottom: 30px;
        max-width: 500px;
      }
    }

    ${Wrapper}.text-alignment---center & {
      > h1,
      > h2 {
        font-size: 56px;
        margin-bottom: 10px;
      }
      p {
        margin-bottom: 40px;
      }
    }
    ${StyledContainer}.reduced-width & {
      margin: auto 0;
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.xl')}) {
    ${Wrapper}.text-left---image-right & {
      p {
        max-width: 100%;
      }
    }
  }
`;

// * Hero image
export const Img = styled(Image)`
  /* Default Styling */
  z-index: 1;
  height: 100%;
  object-fit: contain;
  max-width: initial;
  width: calc(100% + 60px);
  margin: 0 -30px;
  border-top: none;

  //for text-alignment---center component img at 425px
  > picture > img {
    object-fit: cover !important;
  }

  /* Default Styling for 50/50 */
  ${StyledContainer}.reduced-width & {
    max-width: initial;
    max-height: 300px;
    width: calc(100% + 60px);
    margin: 0 -30px;
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    /* Default Styling */
    align-self: flex-end;
    height: auto;
    margin: 0;
    width: 35%;
    max-height: 385px;
    display: none;

    /* Default Styling for 50/50 */
    ${StyledContainer}.reduced-width & {
      width: 100%;
      margin: 0;
      width: 85%;
      max-height: 385px;
      margin-right: -10%;
    }

    /* Styling for image Inside grid option */
    ${StyledContainer}.inside &, ${StyledContainer}.Inside & {
      margin-right: 0 !important;
      /* width: 50% !important; */
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.lg')}) {
    height: 90%;
    width: 25%;
    max-width: 100%;
    max-height: 395px;
    min-width: 280px;

    /* Default Styling for 50/50 */
    ${StyledContainer}.reduced-width & {
      height: 90%;
      width: 50%;
      max-height: 512px;
      max-width: 100%;
      min-width: auto;
    }

    /* Styling for image Inside grid option */
    ${StyledContainer}.inside &, ${StyledContainer}.Inside & {
      width: 50% !important;
      max-height: 500px;
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.max')}) {
    /* Default Styling for 50/50 */
    ${StyledContainer}.reduced-width & {
      margin-right: -10%;
      max-width: 659px;
      width: 60%;
    }
  }
`;

// * Container for bottom content under CTA
export const BottomContent = styled.div`
  padding-top: 12px;
  width: 90%;

  p {
    font-weight: 600;
    ${Wrapper}.text-alignment---center & {
      margin: 0px;
      font-size: 16px;
      display: flex;
      justify-content: center;
      @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
        font-size: 18px;
      }
    }
  }

  @media only screen and (min-width: ${themeGet('breakpoints.md')}) {
    width: 100%;
  }
`;
